/**
 * s237 (chapter three PR 2; D-236.61) — chapter three's board data, typed, and its readers.
 *
 * `BOARD_STEPS_CH3` is GENERATED from the committed `Chapter3.dc.html` (`npm run board:generate`) and checked against it on
 * every push (`npm run check:board-parity`), as chapters one and two are.
 *
 * Chapter three is drawn in chapter ONE's shape (tiles in `groups`, one table per screen and an optional `second`), so its
 * types are chapter one's `BoardStep`, WIDENED where chapters 3–6 draw more (the plan's §B "widened types"):
 *   • an option may carry its brand mark (`{ label, mark, color }`, Coverage's lists) — not only a word;
 *   • `rules` are not only booleans: a tile may swap a cell's list (`opts`), give a cell its own example (`ph`), or turn a
 *     picker into the company card (`card`); and the `second` table carries its own `rules`;
 *   • `pairNone` (one tile and its none side by side) and `noLead` (a table with no lead column, D-237.25).
 * Chapter one's own types are untouched (./board), so nothing it reads changes.
 *
 * 🔴 The same limit as ./board: the board carries look and words, never where an answer is stored (./board-map).
 */
import { BOARD_STEPS_CH3 } from "./board-steps-ch3.generated";
import type { BoardGroup, BoardHeader } from "./board";

/** A board option: its word, or its word with the brand's mark (Chapter3.dc.html WORK_INST, SHARE_PLANS, GOV_PROGS). */
export type BoardOpt = string | { label: string; mark?: string; color?: string };
export type TileCellKind = "joint" | "picker" | "text";
export type TileCell = {
  kind: TileCellKind;
  k: string;
  title?: string;
  word?: string;
  undo?: string;
  ph?: string;
  opts?: BoardOpt[];
  otherLabel?: string;
  otherPh?: string;
  org?: boolean;
  long?: boolean;
  bigTitle?: string;
  bigHint?: string;
};
/** A tile's rules for a table's cells: `false` turns a cell off ("not asked"); `opts` swaps the picker's list; `ph` gives a
 *  cell the tile's own example; `card` makes the picker the company card (D-236.63(1)). */
export type TileRule = { [cellKey: string]: boolean | BoardOpt[] | Record<string, string> | undefined } & {
  opts?: BoardOpt[];
  ph?: Record<string, string>;
  card?: boolean;
};
export type TileTable = {
  cols: string;
  headers: BoardHeader[];
  cells: TileCell[];
  rowsLead?: string;
  rowsNote?: string;
  rules?: Record<string, TileRule>;
};
export type TileStep = Partial<TileTable> & {
  key: string;
  crumb: string;
  title: string;
  why?: string;
  groups?: BoardGroup[];
  pairNone?: boolean;
  noLead?: boolean;
  noDup?: string[];
  second?: TileTable & { tiles: string[] };
  noteFor?: Record<string, string>;
  nextNote?: string;
};

export const BOARD_CH3: readonly TileStep[] = BOARD_STEPS_CH3 as unknown as readonly TileStep[];

/** A chapter-three step by key. Throws on an unknown key — a screen reading a step the board lacks is a bug. */
export function boardStep3(key: string): TileStep {
  const s = BOARD_CH3.find((x) => x.key === key);
  if (!s) throw new Error(`board (chapter three): no screen "${key}"`);
  return s;
}

/** An option's word. Pure. */
export const optLabel = (o: BoardOpt): string => (typeof o === "string" ? o : o.label);

/** The table a tile's rows are drawn in: the `second` table when the tile is one of its tiles, else the step's own. Pure. */
export function tableOf(step: TileStep, tileId: string): TileTable & { second: boolean } {
  if (step.second && step.second.tiles.includes(tileId)) return { ...step.second, second: true };
  return { cols: step.cols ?? "", headers: step.headers ?? [], cells: step.cells ?? [], rowsLead: step.rowsLead, rowsNote: step.rowsNote, rules: step.rules, second: false };
}

/** The tile's rule for one table, or null. Pure. */
export function ruleOf(table: Pick<TileTable, "rules">, tileId: string): TileRule | null {
  return table.rules?.[tileId] ?? null;
}

/** Rule 45: a cell this tile does not ask ("not asked here"), declared `false` in the tile's rule. Pure. */
export function cellOffFor(table: Pick<TileTable, "rules">, tileId: string, k: string): boolean {
  return ruleOf(table, tileId)?.[k] === false;
}

/** The cell's example for this tile: the tile's own (`rules[tile].ph[k]`), else the cell's. Pure. */
export function cellPhFor(table: Pick<TileTable, "rules">, tileId: string, cell: Pick<TileCell, "k" | "ph">): string | undefined {
  return ruleOf(table, tileId)?.ph?.[cell.k] ?? cell.ph;
}

/** The picker's list for this tile: the tile's own (`rules[tile].opts`), else the cell's. Pure. */
export function cellOptsFor(table: Pick<TileTable, "rules">, tileId: string, cell: Pick<TileCell, "opts">): BoardOpt[] {
  return ruleOf(table, tileId)?.opts ?? cell.opts ?? [];
}
